import React, { useRef, forwardRef } from 'react';
import { Highlighter } from '../ui/Highlighter';
import { AnimatedBeam } from '../ui/AnimatedBeam';
import { cn } from '../../lib/utils';

const Circle = forwardRef(({ className, children }, ref) => {
  return (
    <div
      ref={ref}
      className={cn(
        "z-10 flex w-6 h-6 items-center justify-center rounded-full border-4 bg-blue-500 border-blue-300 shadow-lg",
        className
      )}
    >
      {children}
    </div>
  )
});

Circle.displayName = "Circle";

const Journey = () => {
  const [shouldShowHighlighter, setShouldShowHighlighter] = React.useState(false);
  const [isJourneyVisible, setIsJourneyVisible] = React.useState(false);
  const journeyRef = React.useRef(null);
  const hasTriggered = React.useRef(false);
  const containerRef = useRef(null);
  
  // Create refs for each timeline item
  const timelineRefs = useRef([]);
  const cardRefs = useRef([]);

  React.useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsJourneyVisible(entry.isIntersecting);
        
        // Only trigger highlighter once when entering the Journey section
        if (entry.isIntersecting && !hasTriggered.current) {
          hasTriggered.current = true;
          setTimeout(() => {
            setShouldShowHighlighter(true);
          }, 300);
        }
      },
      { 
        threshold: 0.2, // Trigger when 20% of the section is visible
        rootMargin: '-10px 0px'
      }
    );

    if (journeyRef.current) {
      observer.observe(journeyRef.current);
    }

    return () => {
      observer.disconnect();
      // Clean up any highlighter SVGs when component unmounts
      setTimeout(() => {
        const svgs = document.querySelectorAll('svg[class*="rough-annotation"]');
        svgs.forEach(svg => {
          if (svg.parentNode) {
            svg.parentNode.removeChild(svg);
          }
        });
      }, 100);
    };
  }, []);

  const journeyData = [
    {
      year: '2020-2023',
      title: 'Diploma in Computer Science',
      description: 'Completed Diploma in Computer Science & Engineering at Parul University. Built foundation in programming fundamentals, data structures, and software development.',
      status: 'completed'
    },
    {
      year: '2023',
      title: 'Started Bachelor\'s Degree',
      description: 'Began Bachelor of Technology in Computer Science & Engineering at Parul University. Deep diving into advanced concepts, algorithms, and modern development practices.',
      status: 'completed'
    },
    {
      year: '2024',
      title: 'Mastered Full Stack Development',
      description: 'Learned React, TypeScript, Node.js, Python, PostgreSQL, and Docker. Built multiple full-stack applications and gained expertise in modern web technologies.',
      status: 'completed'
    },
    {
      year: 'Jul 2024',
      title: 'AI/ML Hackathon - Health Assistant',
      description: 'Participated in AI/ML Hackathon 1.0 organized by myOnsite Healthcare at Parul University. Developed an innovative health assistant solution and made significant contributions to healthcare technology.',
      status: 'completed'
    },
    {
      year: 'Apr-Jul 2025',
      title: 'First Industry Experience',
      description: 'Worked as Full Stack Developer Intern at DEC Gaming Studio. Gained hands-on experience with real-world projects and collaborative development practices.',
      status: 'completed'
    },
    {
      year: '2026',
      title: 'Graduation & Beyond',
      description: 'Completing Bachelor\'s degree and planning to pursue advanced specializations in AI/ML, cloud architecture, or contribute to major open-source projects.',
      status: 'upcoming'
    }
  ];

  return (
    <div ref={journeyRef} className="min-h-screen py-12 px-4 sm:px-6 lg:px-8 journey-container">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12 md:mb-16">
          <h1 className="text-4xl sm:text-5xl md:text-7xl font-bold text-white mb-4 journey-text">
            My Journey
          </h1>
          <p className="text-lg sm:text-xl text-gray-300 max-w-2xl mx-auto px-4 journey-text">
            {shouldShowHighlighter && isJourneyVisible ? (
              <>
                <Highlighter action="underline" color="#FF9800" animationDuration={1000}>
                  A timeline of my growth as a developer
                </Highlighter>
                <span> and </span>
                <Highlighter action="highlight" color="#87CEEB" animationDuration={800}>
                  tech enthusiast
                </Highlighter>
              </>
            ) : (
              "A timeline of my growth as a developer and tech enthusiast"
            )}
          </p>
        </div>

        {/* Timeline */}
        <div className="relative" ref={containerRef}>
          {/* Timeline Line */}
          <div className="absolute left-6 sm:left-8 md:left-1/2 transform md:-translate-x-1/2 h-full w-0.5 sm:w-1 bg-gradient-to-b from-blue-500 via-purple-500 to-gray-600"></div>

          {/* Timeline Items */}
          {journeyData.map((item, index) => (
            <div key={index} className={`relative flex items-start journey-item mb-6 sm:mb-8 md:mb-12 ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}>
              {/* Timeline Dot */}
              <div
                ref={(el) => (timelineRefs.current[index] = el)}
                className={`absolute left-6 sm:left-8 md:left-1/2 transform -translate-x-1/2 md:-translate-x-1/2 top-6 sm:top-8 w-4 h-4 sm:w-6 sm:h-6 rounded-full border-2 sm:border-4 z-10 ${
                  item.status === 'completed' ? 'bg-green-500 border-green-300' :
                  item.status === 'current' ? 'bg-green-500 border-green-300' :
                  'bg-gray-500 border-gray-400'
                }`}
              ></div>

              {/* Content Card */}
              <div className={`ml-14 sm:ml-16 md:ml-0 md:w-5/12 w-full max-w-none sm:max-w-sm md:max-w-none ${index % 2 === 0 ? 'md:mr-auto md:pr-8' : 'md:ml-auto md:pl-8'}`}>
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 sm:p-6 border border-white/20 hover:bg-white/15 transition-all duration-300 relative journey-card">
                  {/* Beam Connection Point - Hidden on mobile for cleaner look */}
                  <div
                    ref={(el) => (cardRefs.current[index] = el)}
                    className={`hidden md:block absolute w-3 h-3 bg-blue-500 rounded-full border-2 border-white opacity-75 ${
                      index % 2 === 0 ? 'right-0 translate-x-1/2' : 'left-0 -translate-x-1/2'
                    } top-1/2 -translate-y-1/2 z-10`}
                  ></div>
                  
                  {/* Year Badge */}
                  <div className={`inline-block px-2 py-1 sm:px-3 sm:py-1 rounded-full journey-year text-xs sm:text-sm font-semibold mb-3 ${
                    item.status === 'completed' ? 'bg-green-500/20 text-green-300 border border-green-500/30' :
                    item.status === 'current' ? 'bg-blue-500/20 text-blue-300 border border-blue-500/30' :
                    'bg-gray-500/20 text-gray-300 border border-gray-500/30'
                  }`}>
                    {item.year}
                  </div>
                  
                  {/* Title */}
                  <h3 className="journey-title text-lg sm:text-xl font-bold text-white mb-2 sm:mb-3">
                    {item.title}
                  </h3>
                  
                  {/* Description */}
                  <p className="journey-description text-sm sm:text-base text-gray-300 leading-relaxed">
                    {item.description}
                  </p>
                  
                  {/* Status Indicator */}
                  <div className={`mt-3 sm:mt-4 flex items-center text-xs sm:text-sm ${
                    item.status === 'completed' ? 'text-green-400' :
                    item.status === 'current' ? 'text-blue-400' :
                    'text-gray-400'
                  }`}>
                    <div className={`w-2 h-2 rounded-full mr-2 ${
                      item.status === 'completed' ? 'bg-green-400' :
                      item.status === 'current' ? 'bg-blue-400 animate-pulse' :
                      'bg-gray-400'
                    }`}></div>
                    {item.status === 'completed' ? 'Completed' :
                     item.status === 'current' ? 'In Progress' : 'Planned'}
                  </div>
                </div>
              </div>
            </div>
          ))}
          
          {/* Animated Beams - Only show on desktop */}
          <div className="hidden md:block">
            {journeyData.map((item, index) => {
              // Create individual refs for each beam
              const timelineRef = { current: timelineRefs.current[index] };
              const cardRef = { current: cardRefs.current[index] };
              
              return (
                <AnimatedBeam
                  key={`beam-${index}`}
                  containerRef={containerRef}
                  fromRef={timelineRef}
                  toRef={cardRef}
                  curvature={0.2}
                  duration={1.5}
                  delay={index * 0.3}
                  gradientStartColor="#60a5fa"
                  gradientStopColor="#a855f7"
                  pathColor="rgba(59, 130, 246, 0.15)"
                  pathWidth={1}
                />
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Journey;